import { inject } from '@angular/core';
import { SignInService } from '../sign-in/sign-in.service';
import { BehaviorSubject, Observable, ReplaySubject, Subject, Subscription } from 'rxjs';
import { EncodingUtils } from '../../encoding-utils';
import { LottoUser } from '../../../components/views/sign-in/user.model';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Ping } from '../../event-dtos';

export abstract class AbstractWsService<T> {
  private readonly signInService = inject(SignInService);
  private readonly jwtHelperService = new JwtHelperService();

  private readonly connected = new BehaviorSubject<boolean>(false);
  private readonly messagesSubject = new ReplaySubject<T>(1);
  private readonly closeSubject = new Subject<number>();
  private currentSubscription: Subscription;
  private webSocket: WebSocketSubject<any>;

  private pingInterval: any;

  protected constructor() {
    this.signInService.initiatedUser.subscribe((d) => {
      if (!!d && !this.connected.getValue()) {
        this.connect(this.signInService.getCurrentUser());
      }
    });
    this.closeSubject.subscribe(() => {
      this.unsubscribeFromWs();
      const user = this.signInService.getCurrentUser();
      if (this.isActive(user.getToken())) {
        this.connect(user);
      }
    });
  }

  public getMessagesObs(): Observable<T> {
    return this.messagesSubject.asObservable();
  }

  protected abstract getPath(): string;

  protected abstract onError(error: any): void;

  private connect(user: LottoUser) {
    if (this.webSocket && !this.webSocket.closed) {
      return;
    }
    const endpoint = this.buildEndpoint(user);

    this.webSocket = webSocket({
      url: endpoint,
      closeObserver: {
        next: (d) => setTimeout(() => this.closeSubject.next(d.code), 3000),
      },
      openObserver: {
        next: () => this.connected.next(true),
      },
    });

    this.pingInterval = setInterval(() => {
      if (!this.webSocket || this.webSocket.closed) {
        clearInterval(this.pingInterval);
        return;
      }
      this.webSocket.next({ cmd: 'ping', data: {} as Ping });
    }, 60_000);

    this.subscribeToWs();
  }

  private unsubscribeFromWs() {
    clearInterval(this.pingInterval);
    if (this.currentSubscription && !this.currentSubscription.closed) {
      this.currentSubscription.unsubscribe();
    }
    if (this.webSocket && !this.webSocket.closed) {
      try {
        this.webSocket.complete();
      } catch (ignored) {}
    }
    this.webSocket = undefined as any;
    this.connected.next(false);
  }

  private isActive(token: string): boolean {
    return token && !this.jwtHelperService.isTokenExpired(token);
  }

  private buildEndpoint(user: LottoUser): string {
    return (
      (location.protocol.includes('https') ? 'wss' : 'ws') +
      '://' +
      window.location.host +
      this.getPath() +
      '?' +
      Object.keys(user.getAuthHeaders())
        .map((k) => k + '=' + user.getAuthHeaders()[k])
        .join('&')
    );
  }

  private subscribeToWs() {
    this.currentSubscription = this.webSocket.pipe().subscribe({
      next: (dataFromServer) => this.messagesSubject.next(EncodingUtils.decodeResponse(dataFromServer)),
      error: (e) => this.onError(e),
    });
  }
}
